// export.ts — CSV export of a vehicle's fill-ups + services (all local; nothing leaves
// the device). The user's own backup of their own log.

import type { Fillup, Service, Vehicle } from "./store";
import { prettyKind } from "./store";

function cell(v: string | number | boolean): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csv(headers: string[], rows: (string | number | boolean)[][]): string {
  return [headers, ...rows].map((r) => r.map(cell).join(",")).join("\n");
}

/** CSV with a fill-ups section and a services section, both for one vehicle. */
export function buildExport(vehicle: Vehicle, fillups: Fillup[], services: Service[]): { csv: string; filename: string } {
  const du = vehicle.distance_unit;
  const vu = vehicle.volume_unit;

  const fillupRows = [...fillups]
    .sort((a, b) => a.odometer - b.odometer)
    .map((f) => [f.ts.slice(0, 10), f.odometer, f.volume, f.cost, f.is_full_tank ? "full" : "partial", f.note ?? ""]);
  const fillupCsv = csv(
    [`Fill-ups — ${vehicle.name}`],
    [],
  ) + "\n" + csv(
    ["Date", `Odometer (${du})`, `Volume (${vu})`, "Cost", "Tank", "Note"],
    fillupRows,
  );

  const serviceRows = [...services]
    .sort((a, b) => a.ts.localeCompare(b.ts))
    .map((s) => [s.ts.slice(0, 10), prettyKind(s.kind), s.odometer, s.cost, s.note ?? ""]);
  const serviceCsv = csv(
    [`Services — ${vehicle.name}`],
    [],
  ) + "\n" + csv(
    ["Date", "Type", `Odometer (${du})`, "Cost", "Note"],
    serviceRows,
  );

  const out = `${fillupCsv}\n\n${serviceCsv}\n`;
  const stamp = new Date().toISOString().slice(0, 10);
  const slug = vehicle.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "vehicle";
  return { csv: out, filename: `garage-${slug}-${stamp}.csv` };
}
